import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import * as s from './styles';

interface TooltipProps {
  children: React.ReactNode;
  title?: string;
  disabled?: boolean;
  className?: string; // allow passing className to wrapper
}

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (value: T) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function') ref(value);
      else (ref as React.MutableRefObject<T | null>).current = value;
    });
  };
}

type AnchorRect = {
  left: number;
  top: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
};

const TOOLTIP_OFFSET_PX = 8;
const VIEWPORT_MARGIN_PX = 8;

const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  ({ children, title, disabled, className }, forwardedRef) => {
    const innerRef = useRef<HTMLDivElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const combinedRef = useMemo(
      () => mergeRefs<HTMLDivElement | null>(innerRef, forwardedRef),
      [forwardedRef]
    );

    const [tooltipState, setTooltipState] = useState<{
      visible: boolean;
      top: number;
      left: number;
      anchor: AnchorRect | null;
    }>({ visible: false, top: 0, left: 0, anchor: null });

    const getAnchorRect = useCallback((): AnchorRect | null => {
      const el = innerRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      return {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        right: rect.right,
        bottom: rect.bottom,
      };
    }, []);

    const computeTooltipPosition = useCallback(
      (anchor: AnchorRect, tooltipWidth: number, tooltipHeight: number) => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const centerX = anchor.left + anchor.width / 2;
        const halfTooltip = tooltipWidth / 2;

        const minCenterX = VIEWPORT_MARGIN_PX + halfTooltip;
        const maxCenterX = viewportWidth - VIEWPORT_MARGIN_PX - halfTooltip;

        const left =
          minCenterX > maxCenterX
            ? viewportWidth / 2
            : Math.min(Math.max(centerX, minCenterX), maxCenterX);

        const belowTop = anchor.bottom + TOOLTIP_OFFSET_PX;
        const aboveTop = anchor.top - TOOLTIP_OFFSET_PX - tooltipHeight;

        const fitsBelow =
          belowTop + tooltipHeight + VIEWPORT_MARGIN_PX <= viewportHeight;
        const fitsAbove = aboveTop >= VIEWPORT_MARGIN_PX;

        const top = fitsBelow
          ? belowTop
          : fitsAbove
            ? aboveTop
            : Math.max(
              VIEWPORT_MARGIN_PX,
              Math.min(
                belowTop,
                viewportHeight - VIEWPORT_MARGIN_PX - tooltipHeight
              )
            );

        return { left, top };
      },
      []
    );

    const updateTooltipPosition = useCallback(() => {
      const anchor = getAnchorRect();
      if (!anchor) return;

      const tooltipEl = tooltipRef.current;
      if (tooltipEl) {
        const tooltipRect = tooltipEl.getBoundingClientRect();
        const { left, top } = computeTooltipPosition(
          anchor,
          tooltipRect.width,
          tooltipRect.height
        );
        setTooltipState((prev) => ({ ...prev, anchor, left, top }));
        return;
      }

      setTooltipState((prev) => ({
        ...prev,
        anchor,
        left: anchor.left + anchor.width / 2,
        top: anchor.bottom + TOOLTIP_OFFSET_PX,
      }));
    }, []);

    const showTooltip = useCallback(() => {
      if (!title || disabled) return;
      updateTooltipPosition();
      setTooltipState((prev) => ({ ...prev, visible: true }));
    }, [disabled, title, updateTooltipPosition]);

    const hideTooltip = useCallback(() => {
      setTooltipState((prev) => ({ ...prev, visible: false, anchor: null }));
    }, []);

    useLayoutEffect(() => {
      if (!tooltipState.visible) return;
      if (!tooltipState.anchor) return;
      const tooltipEl = tooltipRef.current;
      if (!tooltipEl) return;

      const tooltipRect = tooltipEl.getBoundingClientRect();
      const { left, top } = computeTooltipPosition(
        tooltipState.anchor,
        tooltipRect.width,
        tooltipRect.height
      );

      setTooltipState((prev) => {
        if (!prev.visible || !prev.anchor) return prev;
        const leftChanged = Math.abs(prev.left - left) > 0.5;
        const topChanged = Math.abs(prev.top - top) > 0.5;
        return leftChanged || topChanged ? { ...prev, left, top } : prev;
      });
    }, [computeTooltipPosition, tooltipState.anchor, tooltipState.visible]);

    useEffect(() => {
      if (!tooltipState.visible) return;

      const onUpdate = () => updateTooltipPosition();
      window.addEventListener('scroll', onUpdate, true);
      window.addEventListener('resize', onUpdate);
      return () => {
        window.removeEventListener('scroll', onUpdate, true);
        window.removeEventListener('resize', onUpdate);
      };
    }, [tooltipState.visible, updateTooltipPosition]);

    return (
      <>
        <s.TooltipWrapper
          ref={combinedRef}
          className={className}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          onFocus={showTooltip}
          onBlur={hideTooltip}
        >
          {children}
        </s.TooltipWrapper>

        {title && tooltipState.visible
          ? createPortal(
            <s.TooltipPortal
              ref={tooltipRef}
              data-visible={tooltipState.visible ? 'true' : 'false'}
              style={{ top: tooltipState.top, left: tooltipState.left }}
            >
              {title}
            </s.TooltipPortal>,
            document.body
          )
          : null}
      </>
    );
  }
);

export default Tooltip;
